import { useNavigate } from 'react-router-dom';
import { COLORS } from '../../constants/theme';
import type { PastWarDto } from '../../types/diplomacy';
import { formatGameDate } from '../../utils/gameTime';
import { AT_WAR_COLOR } from './relationsPresentation';

interface Props {
  warHistory: PastWarDto[];
  layout: 'table' | 'stacked';
}

const END_REASON_LABELS: Record<string, string> = {
  MUTUAL_PEACE: 'Mutual Peace',
  WITHDRAWAL: 'Withdrawal',
  AUTO_VICTORY: 'Auto-Victory',
};

function outcome(w: PastWarDto): { color: string; label: string } {
  if (w.weWon == null) return { color: COLORS.text.secondary, label: 'Draw' };
  return w.weWon ? { color: COLORS.positive, label: 'Victory' } : { color: AT_WAR_COLOR, label: 'Defeat' };
}

function duration(w: PastWarDto): number | null {
  return w.endedAtTick != null ? w.endedAtTick - w.declaredAtTick : null;
}

function endReasonLabel(w: PastWarDto): string {
  return w.endReason != null ? END_REASON_LABELS[w.endReason] ?? w.endReason : '—';
}

export function WarHistoryTable({ warHistory, layout }: Props) {
  const navigate = useNavigate();

  if (warHistory.length === 0) {
    return (
      <p style={{ color: COLORS.text.tertiary, fontSize: 13 }}>
        No war history.
      </p>
    );
  }

  const opponentLink = (w: PastWarDto) => (
    <button
      onClick={() => navigate(`/world/dossier/${w.opponentKingdomId}`)}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        color: COLORS.link,
        cursor: 'pointer',
        fontSize: 13,
        textAlign: 'left',
      }}
    >
      {w.opponentKingdomName ?? w.opponentKingdomId} ({w.opponentKingdomId})
    </button>
  );

  if (layout === 'stacked') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {warHistory.map((w) => {
          const { color, label } = outcome(w);
          const d = duration(w);
          return (
            <div
              key={w.warId}
              style={{
                padding: 12,
                backgroundColor: COLORS.card,
                border: `1px solid ${COLORS.border}`,
                borderRadius: 6,
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                {opponentLink(w)}
                <span style={{ color, fontWeight: 600, fontSize: 12, flexShrink: 0 }}>{label}</span>
              </div>
              <div style={{ fontSize: 12, color: COLORS.text.tertiary, marginTop: 4 }}>
                Declared {formatGameDate(w.declaredAtTick)} · {d != null ? `${d} days` : '—'} · {endReasonLabel(w)}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const thStyle: React.CSSProperties = {
    padding: '6px 8px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 600,
    color: COLORS.text.secondary,
    backgroundColor: COLORS.sectionHeader,
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            <th style={thStyle}>Opponent</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Declared</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Duration</th>
            <th style={thStyle}>Outcome</th>
            <th style={thStyle}>Result</th>
          </tr>
        </thead>
        <tbody>
          {warHistory.map((w, i) => {
            const d = duration(w);
            const { color, label } = outcome(w);

            return (
              <tr
                key={w.warId}
                style={{ backgroundColor: i % 2 === 1 ? COLORS.tableRowAlt : undefined }}
              >
                <td style={{ padding: '6px 8px', color: COLORS.text.primary }}>{opponentLink(w)}</td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: COLORS.text.secondary }}>
                  {formatGameDate(w.declaredAtTick)}
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right', color: COLORS.text.secondary }}>
                  {d != null ? `${d} days` : '—'}
                </td>
                <td style={{ padding: '6px 8px', color: COLORS.text.secondary }}>{endReasonLabel(w)}</td>
                <td style={{ padding: '6px 8px' }}>
                  <span style={{ color, fontWeight: 600, fontSize: 12 }}>{label}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
